import { ParentStep } from '@utils/enums/ParentStep.enum';
import { StepsStep } from '@utils/enums/StepsStep.enum';
import {
  FormConfigurationDTO,
  GeneralConfigDTO,
  GeneralInfoDTO,
  ScheduleConfigDTO,
} from '@utils/interfaces';
import { Links } from 'src/embedded-app/EmbeddedApp';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export interface InsuranceInfo {
  visionInsurancePlan?: string;
  visionInsurance?: string;
  otherInsurance?: string;
}

export interface ScheduleInfo {
  date: string;
  hour: string;
  doctor: string;
}

export interface PhoneInfo {
  prefix: string;
  number: number;
  type: 'mobile' | 'landline';
  country: string;
}

export interface InfoUser {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  email: string;
  phoneNumber: PhoneInfo;
}

export interface OnlineBookingStore {
  flowMode?: string;
  integrationMode?: string;
  practiceId: string;
  doctorId: string;
  appointmentId: string | undefined;
  showAddPatientFrame: boolean;
  shouldSave: boolean;
  generalInfo: GeneralInfoDTO | undefined;
  generalConfig: GeneralConfigDTO | undefined;
  scheduleConfig: ScheduleConfigDTO | undefined;
  isStartedNewPatient: boolean;
  formConfigs: FormConfigurationDTO | undefined;
  currentFormStep: StepsStep;
  currentBookStep: ParentStep;
  currentPatient: number;
  locationData?: string;
  insuranceData?: InsuranceInfo;
  contactDetailsCheckboxStatus?: boolean;
  isRescheduling?: boolean;
  showTyp?: boolean;
  appTypeIdFromUrl?: string;
  providerIdFromUrl?: string;
  brandName?: string;
  jwtToken?: string;
  infoUser?: InfoUser;
  pageCountry?: string;
  pageLanguage?: string;
  links?: Links;
  rescheduleUrl?: string;
  cancelUrl?: string;
  hideBackToBrandHomePage?: boolean;
  refid?: string;
  setRescheduleUrl: (val?: string) => void;
  setCancelUrl: (val?: string) => void;
  setPageCountry: (val?: string) => void;
  setPageLanguage: (val?: string) => void;
  setShowTyp: (val: boolean) => void;
  setCurrentPatient: (val: number) => void;
  setShowAddPatientFrame: (val: boolean) => void;
  setShouldSave: (val: boolean) => void;
  setIsNewPatientStarted: (val: boolean) => void;
  setFlowMode: (val: string) => void;
  setIntegrationMode: (val: string) => void;
  setPracticeId: (val: string) => void;
  setDoctorId: (val: string) => void;
  setAppointmentId: (val?: string) => void;
  setGeneralInfo: (val: GeneralInfoDTO) => void;
  setGeneralConfig: (val: GeneralConfigDTO) => void;
  setScheduleConfig: (val: ScheduleConfigDTO) => void;
  setFormConfigs: (val: FormConfigurationDTO) => void;
  setFormStep: (val: StepsStep) => void;
  setBookStep: (val: ParentStep) => void;
  setContactDetailsCheckboxStatus: (val: boolean) => void;
  setIsRescheduling: (val: boolean) => void;
  setAppTypeIdFromUrl: (val?: string) => void;
  setProviderIdFromUrl: (val?: string) => void;
  setBrandName: (val?: string) => void;
  setJwtToken: (val?: string) => void;
  reset: () => void;
  getFormConfig: () => FormConfigurationDTO | undefined;
  getShouldSave: () => boolean;
  getGeneralInfo: () => GeneralInfoDTO | undefined;
  setInfoUser: (val?: InfoUser) => void;
  setLinks: (val?: Links) => void;
  setHideBackToBrandHomePage: (val?: boolean) => void;
  setRefid: (val?: string) => void;
}

const initialState = {
  flowMode: undefined,
  practiceId: '',
  doctorId: '',
  appointmentId: undefined,
  showAddPatientFrame: false,
  shouldSave: false,
  isStartedNewPatient: false,
  generalInfo: undefined,
  generalConfig: undefined,
  scheduleConfig: undefined,
  formConfigs: undefined,
  pageCountry: undefined,
  pageLanguage: undefined,
  rescheduleUrl: undefined,
  cancelUrl: undefined,
  // TODO rimuovere
  currentFormStep: StepsStep.APPOINTMENT_TYPE,
  // TODO rimuovere
  currentBookStep: ParentStep.STEPS,
  currentPatient: 0,
  contactDetailsCheckboxStatus: true,
  brandName: undefined,
  jwtToken: undefined,
  locationData: undefined,
  insuranceData: undefined,
  isRescheduling: undefined,
  showTyp: undefined,
  appTypeIdFromUrl: undefined,
  providerIdFromUrl: undefined,
  infoUser: undefined,
  refid: undefined,
};

export const useOnlineBookingStore = create(
  persist<OnlineBookingStore>(
    (set, get) => ({
      ...initialState, // Applica i valori iniziali allo store
      getFormConfig: () => {
        return get().formConfigs;
      },
      getShouldSave: () => get().shouldSave,
      getGeneralInfo: () => get().generalInfo,
      setCurrentPatient: (val: number) => {
        set(state => {
          console.log('#setNewPatient', val);
          return {
            ...state,
            currentPatient: val,
          };
        });
      },

      setShowAddPatientFrame: (val: boolean) => {
        set(state => {
          return {
            ...state,
            showAddPatientFrame: val,
          };
        });
      },

      setIsRescheduling: (val: boolean) => {
        set(state => {
          return {
            ...state,
            isRescheduling: val,
          };
        });
      },

      setShowTyp: (val: boolean) => {
        set(state => {
          return {
            ...state,
            showTyp: val,
          };
        });
      },

      setShouldSave: (val: boolean) => {
        set(state => {
          return {
            ...state,
            shouldSave: val,
          };
        });
      },

      setIsNewPatientStarted: (val: boolean) => {
        set(state => {
          return {
            ...state,
            isStartedNewPatient: val,
          };
        });
      },

      setGeneralInfo: (val: GeneralInfoDTO) => {
        set(state => {
          return {
            ...state,
            generalInfo: {
              ...val,
              /**
               * Se la brand name è presente, aggiungiamo la location al general info
               * per far si che la location sia visibile nella logiche
               */
              ...(state.brandName ? { location: state.practiceId } : {}),
            },
          };
        });
      },

      setScheduleConfig: (val: ScheduleConfigDTO) => {
        set(state => {
          return {
            ...state,
            scheduleConfig: val,
          };
        });
      },

      setFormConfigs: (val: FormConfigurationDTO) => {
        set(state => {
          return {
            ...state,
            formConfigs: val,
          };
        });
      },

      setGeneralConfig: (val: GeneralConfigDTO) => {
        set(state => {
          return {
            ...state,
            generalConfig: val,
          };
        });
      },

      setFlowMode: (val: string) => {
        set(state => {
          return {
            ...state,
            flowMode: val,
          };
        });
      },
      setIntegrationMode: (val: string) => {
        set(state => {
          return {
            ...state,
            integrationMode: val,
          };
        });
      },
      setPracticeId: (val: string) => {
        set(state => {
          return {
            ...state,
            practiceId: val,
          };
        });
      },
      setDoctorId: (val: string) => {
        set(state => {
          return {
            ...state,
            doctorId: val,
          };
        });
      },
      setAppointmentId: (val?: string) => {
        set(state => {
          return {
            ...state,
            appointmentId: val,
          };
        });
      },
      setFormStep: (val: number) => {
        set(state => {
          return {
            ...state,
            currentFormStep: val,
          };
        });
      },
      setBookStep: (val: ParentStep) => {
        set(state => {
          return {
            ...state,
            currentBookStep: val,
          };
        });
      },
      setContactDetailsCheckboxStatus: (val: boolean) => {
        set(state => {
          return {
            ...state,
            contactDetailsCheckboxStatus: val,
          };
        });
      },
      setAppTypeIdFromUrl: (val?: string) => {
        set(state => {
          return {
            ...state,
            appTypeIdFromUrl: val,
          };
        });
      },
      setProviderIdFromUrl: (val?: string) => {
        set(state => {
          return {
            ...state,
            providerIdFromUrl: val,
          };
        });
      },
      setBrandName: (val?: string) => {
        set(state => {
          return {
            ...state,
            brandName: val,
          };
        });
      },
      setJwtToken: (val?: string) => {
        set(state => {
          return {
            ...state,
            jwtToken: val,
          };
        });
      },
      // Funzione per resettare lo stato, mantiene solo la customization per i colori personalizzati
      reset: () => {
        set(state => {
          return {
            ...initialState,
            practiceId: state.practiceId,
            generalConfig: {
              customization: state.generalConfig?.customization,
            },
          };
        });
      },
      setInfoUser: (val?: InfoUser) => {
        set(state => {
          return {
            ...state,
            infoUser: val,
          };
        });
      },
      setPageCountry: (val?: string) => {
        set(state => ({
          ...state,
          pageCountry: val,
        }));
      },
      setPageLanguage: (val?: string) => {
        set(state => ({
          ...state,
          pageLanguage: val,
        }));
      },
      setRescheduleUrl: (val?: string) => {
        set(state => ({
          ...state,
          rescheduleUrl: val,
        }));
      },
      setCancelUrl: (val?: string) => {
        set(state => ({
          ...state,
          cancelUrl: val,
        }));
      },
      setLinks: (val?: Links) => {
        set(state => ({
          ...state,
          links: val,
        }));
      },
      setHideBackToBrandHomePage: (val?: boolean) => {
        set(state => ({
          ...state,
          hideBackToBrandHomePage: val,
        }));
      },
      setRefid: (val?: string) => {
        set(state => ({
          ...state,
          refid: val,
        }));
      },
    }),
    {
      name: 'bookingProgression',
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);
